<template>
  <component is="style" v-if="imageNormalStyling" type="text/css">
    {{ imageNormalStyling }}
  </component>
  <component is="style" v-if="imageHoverStyling && allowHoverStyling" type="text/css">
    {{ imageHoverStyling }}
  </component>
  <div v-if="src" class="lf-image" :style="model.state.elementStyling?.wrapper">
    <img
      v-if="src"
      class="lf-image__img"
      :style="model.state.elementStyling?.inline"
      :class="imgClassList"
      :src="src"
      :alt="model.state.altText"
      :title="model.state.altText"
      @mouseenter="onMouseOver"
      @mouseleave="onMouseOut"
      @click="onImageClick($event)"
    />
  </div>
</template>

<script lang="ts">
import { AddonLfImageModel, ImageFilterState } from '@/components/addons/lf-image/Model';
import { computed, defineComponent, onMounted, PropType, ref, watch } from 'vue';
import { applyReplacementTags, capitalizeFirstLetter, isSsr, preloadImagePromise } from '@/utilities/Utilities';
import { ImageSourceType } from '@/components/addons/lf-image/Data';
import { ActionType, FieldType, KeyBinding, SectionType } from '@/typings/enums/enums';
import { isValidHttpUrl } from '@/utilities/Url';
import { AddonRegistrationModel } from '@/components/addons/registration/Model';
import { setFormReplacementTags, submitForm } from '@/utilities/Registration';
import { FormDisableDefaultBehavior } from '@/exceptions/FormDisableDefaultBehavior';
import { useStore } from '@/store';
import useDevice from '@/hooks/useDevice';

export default defineComponent({
  name: 'AddonLfImage',
  inheritAttrs: false,
  props: {
    model: {
      type: Object as PropType<AddonLfImageModel>,
      required: true
    }
  },
  setup(props) {
    const store = useStore();
    const state = (props.model as AddonLfImageModel).state;
    const playHoverAnimation = ref(false);
    const imageNotLoaded = ref(false);
    let enterAnimationInProgress = false;
    let allowHoverStyling = ref(true);
    const didSubmit = ref(false);
    const { isMobile } = useDevice();

    if (isMobile) {
      allowHoverStyling.value = false;
    }

    const onImageClick = async (event?: Event) => {
      if (didSubmit.value) return;
      if (!didSubmit.value) {
        didSubmit.value = true;
      }
      // We should only stop propagation if we actually have an action.
      if (event && state.action?.state.type) {
        event.stopPropagation();
      }

      /**
       * If the image is "acting" as a submit button, we need to submit the form
       * If not, we fallback to the default action of the button
       */
      if (
        state.action?.state.type &&
        [ActionType.FORCE_SKIP_REGISTRATION, ActionType.GOTO_FLOW_PAGE].includes(state.action.state.type)
      ) {
        const hasRegistrationAddon = props.model.getSection().getAddons<AddonRegistrationModel>('registration');
        if (hasRegistrationAddon.length > 0 && state.action?.state.type === ActionType.GOTO_FLOW_PAGE) {
          const section = props.model.getSection();
          let allowsForSubmit = true;

          const flowRegistrationInfo = store.getters['global/flowRegistrationInfo'];
          setFormReplacementTags();

          if (
            !flowRegistrationInfo &&
            section.getSectionType() === SectionType.FLOWPAGE &&
            !section.getAddons<AddonRegistrationModel>('registration').some((registrationAddon) => {
              return registrationAddon.state.fields?.some((field) => field.state.type === FieldType.EMAIL);
            })
          ) {
            allowsForSubmit = false;
          }
          if (allowsForSubmit) {
            try {
              await submitForm(section);
              state.action?.triggerAction(props.model, props.model.column.replacementTags);
            } catch (e) {
              if (e instanceof FormDisableDefaultBehavior) {
                throw new Error('Cant submit form [FormDisableDefaultBehavior]' + e);
              } else {
                // If exception wasn't the exception to disable default behavior we should bubble up the exception.
                throw e;
              }
            }
          }
        } else {
          state.action?.triggerAction(props.model, props.model.column.replacementTags);
        }
      } else {
        state.action?.triggerAction(props.model, props.model.column.replacementTags);
      }
      didSubmit.value = false;
    };

    const src = computed(() => {
      const replacementTagSrc = applyReplacementTags(
        state.sourceType === ImageSourceType.BROWSE ? state.src ?? '' : `#${state.replacementTag ?? ''}#`,
        props.model.column.replacementTags
      );
      let result = replacementTagSrc;
      // checking that its not an url and instead <img tag>
      // if img tag then we will extract the src and return the result
      if (replacementTagSrc && !isValidHttpUrl(replacementTagSrc)) {
        const regex = /<img.*?src="(.*?)"/g;
        result = regex.exec(replacementTagSrc)?.[1] ?? replacementTagSrc;
      }

      if (!isValidHttpUrl(result)) {
        return '';
      }

      return result;
    });

    const generateFilterStyles = (data: ImageFilterState) => {
      const filterState = data;
      if (!filterState) {
        return undefined;
      }

      let filterStyle = '';
      filterStyle += ` opacity(${filterState.opacity * 100}%) `;
      filterStyle += ` blur(${filterState.blur}px) `;
      filterStyle += ` contrast(${filterState.contrast}%) `;
      filterStyle += ` saturate(${filterState.saturation}%) `;
      filterStyle += ` hue-rotate(${filterState.hue}deg) `;
      filterStyle += ` brightness(${filterState.brightness}%) `;
      return filterStyle;
    };

    const imageFilterStylesNormal = computed(() => {
      const filterState = state.settings?.layout?.filters?.normal;
      if (filterState) {
        return generateFilterStyles(filterState);
      }
      return undefined;
    });

    const imageFilterStylesHover = computed((): string | undefined => {
      const filterState = state.settings?.layout?.filters?.hover;
      if (filterState) {
        return generateFilterStyles(filterState);
      }
      return undefined;
    });

    const generateStyling = (imageTransition?: string, filterStyles?: string) => {
      let style = '';
      if (filterStyles === 'normal') {
        style += `
        -webkit-filter: ${imageFilterStylesNormal.value};
        filter: ${imageFilterStylesNormal.value};
      `;
      }
      if (filterStyles === 'hover') {
        style += `
        -webkit-filter: ${imageFilterStylesHover.value};
        filter: ${imageFilterStylesHover.value};
      `;
      }
      if (imageTransition && filterStyles) {
        style += `
        -webkit-transition: -webkit-${imageTransition};
        transition: -webkit-${imageTransition};
        -o-transition: ${imageTransition};
        transition: ${imageTransition};
        transition: ${imageTransition}, -webkit-${imageTransition};
      `;
      }
      return style;
    };

    const imageNormalStyling = computed(() => {
      const imageTransition = state.elementStyling?.normal?.transition ?? 'filter 1s ease';
      let style = generateStyling(imageTransition, 'normal');
      if (style && style !== '') {
        return style ? `.${state.classIdentifier} .lf-image img { ${style} }` : '';
      } else {
        return undefined;
      }
    });

    const imageHoverStyling = computed(() => {
      const imageTransition = state.elementStyling?.hover?.transition ?? 'filter 1s ease';
      let style = generateStyling(imageTransition, 'hover');
      if (style && style !== '') {
        return style ? `.${state.classIdentifier} .lf-image img:hover { ${style} }` : '';
      } else {
        return undefined;
      }
    });

    const doKeybinding = (e: KeyboardEvent) => {
      if (e.key === capitalizeFirstLetter(KeyBinding.ENTER) || e.key === capitalizeFirstLetter(KeyBinding.ESCAPE)) {
        onImageClick();
      }
    };

    const initKeybindings = () => {
      if (!isSsr()) {
        if (
          state.action &&
          state.action.state.keybinding &&
          (state.action.state.keybinding === KeyBinding.ENTER || state.action.state.keybinding === KeyBinding.ESCAPE)
        ) {
          window.addEventListener('keydown', doKeybinding);
        }
      }
    };

    const onMouseOver = () => {
      if (!enterAnimationInProgress) {
        playHoverAnimation.value = true;
        enterAnimationInProgress = true;

        setTimeout(
          () => {
            enterAnimationInProgress = false;
            playHoverAnimation.value = false;
          },
          props.model.state.settings?.layout?.filters?.hover.enterDuration
            ? props.model.state.settings.layout.filters.hover.enterDuration * 1000
            : 525
        );
      }
    };

    const onMouseOut = () => {
      if (!enterAnimationInProgress) {
        playHoverAnimation.value = false;
      }
    };

    onMounted(async () => {
      if (readyPromiseResolve) {
        if (src.value) {
          // waiting to load image before transition
          try {
            await preloadImagePromise(src.value);
            readyPromiseResolve();
          } catch (error) {
            imageNotLoaded.value = true;
            // eslint-disable-next-line
            console.error(`Could not load the image, ${src.value}`);
            // TODO: Decide and implement better error handling when image is not loaded.
            readyPromiseResolve();
          }
        } else {
          readyPromiseResolve();
        }
      }
      state.action?.startAutoTrigger();
    });

    watch(
      () => state.action?.state?.keybinding,
      () => {
        if (!isSsr()) {
          window.removeEventListener('keydown', doKeybinding);
        }
        initKeybindings();
      }
    );

    let readyPromiseResolve: (() => void) | undefined;
    const readyPromise = new Promise<void>((resolve) => {
      readyPromiseResolve = resolve;
    });

    const imgClassList = computed(() => {
      return {
        ...props.model.state.imageClasses,
        ...(playHoverAnimation.value &&
          props.model.state.settings?.layout?.filters?.hover.enter && {
            animated: true,
            [`${props.model.state.settings.layout.filters.hover.enter}`]: true
          }),
        'lf-image__img--not-loaded': imageNotLoaded.value
      };
    });

    return {
      allowHoverStyling,
      imageNormalStyling,
      imageHoverStyling,
      onImageClick,
      src,
      imgClassList,
      onMouseOver,
      onMouseOut,
      onBeforeEnter: async () => {
        await readyPromise;
      }
    };
  }
});
</script>

<style lang="scss">
.lf-image {
  display: block;
  font-size: 0;
  line-height: 0;

  &__img {
    display: inline-block;
    border: 0;
    .grid__addon--position-absolute:not(.grid__addon--width-custom) & {
      max-width: none;
    }

    &--clickable {
      cursor: pointer;
    }
    &--not-loaded {
      height: 0;
      visibility: hidden;
    }
  }

  &__placeholder {
    display: inline-block;
    position: relative;
    width: 100%;
    height: 0;
    padding-bottom: 60%;
    background: linear-gradient(to bottom, #cccccc, #e2e2e2);
    color: #383838;

    .fa {
      display: block;
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translateX(-50%) translateY(-50%);
      font-size: 5rem;
      opacity: 0.1;
    }

    &--clickable {
      cursor: pointer;
    }
  }
}

.site--ie .grid__addon--lf-image {
  flex-shrink: 0;
}
</style>
